import api from "./api"

// Generic client-side helper for the async CSV-import job pattern shared by
// every import page (openspec/changes/csv-import-workflows-and-location-fields):
// the bulk-create endpoint now only returns a jobId, and the actual
// created/skipped result is fetched by polling GET /api/import-jobs/:id.
// Polling interval matches the existing precedent in VideosManager.tsx.

export type ImportJobStatus = "queued" | "running" | "completed" | "failed"

export type ImportJob<TCreated = unknown, TSkipped = unknown> = {
  importType: string
  status: ImportJobStatus
  totalRows: number
  processedRows: number
  created: TCreated[]
  skipped: TSkipped[]
}

export async function getImportJob<TCreated = unknown, TSkipped = unknown>(
  jobId: string
): Promise<ImportJob<TCreated, TSkipped>> {
  const res = await api.get(`/import-jobs/${jobId}`)
  return res.data.data
}

const DEFAULT_POLL_INTERVAL_MS = 5000

/**
 * Polls the job's status every `intervalMs` (default 5s, same as
 * VideosManager.tsx) until it reaches `completed`/`failed`, calling
 * `onProgress` after every poll. Returns a `stop()` to cancel early (e.g.
 * on unmount) and the `promise` that resolves with the final job.
 */
export function pollImportJob<TCreated = unknown, TSkipped = unknown>(
  jobId: string,
  onProgress: (job: ImportJob<TCreated, TSkipped>) => void,
  intervalMs = DEFAULT_POLL_INTERVAL_MS
): { stop: () => void; promise: Promise<ImportJob<TCreated, TSkipped>> } {
  let stopped = false
  let timer: ReturnType<typeof setTimeout> | undefined

  const promise = new Promise<ImportJob<TCreated, TSkipped>>((resolve, reject) => {
    async function tick() {
      if (stopped) return
      try {
        const job = await getImportJob<TCreated, TSkipped>(jobId)
        if (stopped) return
        onProgress(job)
        if (job.status === "completed" || job.status === "failed") {
          resolve(job)
          return
        }
      } catch (err) {
        reject(err)
        return
      }
      timer = setTimeout(tick, intervalMs)
    }
    void tick()
  })

  return {
    stop: () => {
      stopped = true
      if (timer) clearTimeout(timer)
    },
    promise,
  }
}
